/**
 * Commercial Account (parent) self-service.
 *
 * A commercial customer (chain HQ / "parent_admin") uses this page to:
 *   - register a commercial group (draft) if they don't have one,
 *   - add / edit / remove child location accounts,
 *   - submit the group for the laundry's approval,
 *   - once approved (active), place orders for any location, manage each
 *     location's recurring schedule, and see spend per location.
 *
 * Ordering for a child is gated server-side (group must be active); this page
 * simply reflects the group's approval status. A logged-in child sees their own
 * orders through the normal flow, not this page.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Container, Heading, Text, VStack, HStack, Button, Input, FormControl,
    FormLabel, Badge, Divider, useToast, Table, Thead, Tbody, Tr, Th, Td,
    IconButton, Alert, AlertIcon, Spinner, Card, CardBody, Textarea, Select,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    ModalCloseButton, useDisclosure, SimpleGrid, Stat, StatLabel, StatNumber,
    Collapse,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { useCustomerAuth } from '../Context/AuthContext';
import AddressAutocompleteInput from '../Components/SiteLanding/AddressAutocompleteInput';
import SubscriptionCard from '../Components/LaundryHome/SubscriptionCard';

const API_URL = process.env.REACT_APP_AWS_API_URL || '';

const STATUS_LABEL = {
    draft: { text: 'Draft — not yet submitted', scheme: 'gray' },
    pending_approval: { text: 'Pending laundry approval', scheme: 'yellow' },
    active: { text: 'Approved & active', scheme: 'green' },
    rejected: { text: 'Not approved', scheme: 'red' },
    suspended: { text: 'Paused by the laundry', scheme: 'orange' },
};

const BUSINESS_TYPES = ['Salon / Spa', 'Gym / Fitness', 'Restaurant', 'Hotel / Rental', 'Medical / Clinic', 'Office', 'Other'];

const authHeaders = () => ({ headers: { 'x-api-key': localStorage.getItem('idToken') || '' } });

const monthRange = (offset = 0) => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: fmt(first), end: fmt(last) };
};

export default function CommercialAccountPage() {
    const { laundryId } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useCustomerAuth();
    const customerId = user?.sub;

    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);       // { groupId, groupName, status, rejectedReason, members: [] }
    const [inGroup, setInGroup] = useState(false);
    const [role, setRole] = useState(null);

    // register form (parent)
    const [groupName, setGroupName] = useState('');
    const [billingEmail, setBillingEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [hqAddress, setHqAddress] = useState('');
    const [contactName, setContactName] = useState('');
    const [businessType, setBusinessType] = useState('');
    const [estLocations, setEstLocations] = useState('');
    const [notes, setNotes] = useState('');
    // add-location form (child)
    const [childLabel, setChildLabel] = useState('');
    const [childEmail, setChildEmail] = useState('');
    const [childPhone, setChildPhone] = useState('');
    const [childAddress, setChildAddress] = useState('');
    const [busy, setBusy] = useState(false);

    // edit-location modal
    const editModal = useDisclosure();
    const [editing, setEditing] = useState(null);
    const [editLabel, setEditLabel] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editAddress, setEditAddress] = useState('');

    // per-location schedule panel
    const [scheduleFor, setScheduleFor] = useState(null);

    // spend breakdown
    const [range, setRange] = useState(monthRange(0));
    const [breakdown, setBreakdown] = useState(null);
    const [breakdownLoading, setBreakdownLoading] = useState(false);

    const load = useCallback(async () => {
        if (!customerId || !laundryId) { setLoading(false); return; }
        try {
            const res = await axios.get(`${API_URL}/api/customer/commercial-group/mine`, {
                params: { customerId, laundryId }, ...authHeaders(),
            });
            const b = res.data || {};
            if (b.status === 'error') {
                toast({ title: b.message || 'Could not load your account', status: 'error', duration: 5000 });
                return;
            }
            setInGroup(!!b.inGroup);
            setRole(b.role || null);
            setGroup(b.inGroup ? b.group : null);
        } catch (e) {
            console.error('load group failed', e);
        } finally {
            setLoading(false);
        }
    }, [customerId, laundryId, toast]);

    useEffect(() => { load(); }, [load]);

    const loadBreakdown = useCallback(async () => {
        if (!customerId || !laundryId) return;
        setBreakdownLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/customer/commercial-group/breakdown`, {
                params: { customerId, laundryId, start: range.start, end: range.end }, ...authHeaders(),
            });
            setBreakdown(res.data?.status === 'success' ? res.data : null);
        } catch (e) {
            setBreakdown(null);
        } finally {
            setBreakdownLoading(false);
        }
    }, [customerId, laundryId, range]);

    const isActiveParent = inGroup && role === 'parent_admin' && group?.status === 'active';
    useEffect(() => { if (isActiveParent) loadBreakdown(); }, [isActiveParent, loadBreakdown]);

    // Endpoints return HTTP 200 with { status: 'error', message } on a handled
    // failure (e.g. ZIP not serviceable), so check the payload status too.
    const call = async (fn, successMsg, failMsg) => {
        setBusy(true);
        try {
            const res = await fn();
            if (res.data?.status && res.data.status !== 'success') {
                toast({ title: res.data.message || failMsg, status: 'error', duration: 7000, isClosable: true });
                return false;
            }
            if (successMsg) toast({ title: successMsg, status: 'success', duration: 2500 });
            await load();
            return true;
        } catch (e) {
            toast({ title: e.response?.data?.message || failMsg, status: 'error', duration: 4000 });
            return false;
        } finally {
            setBusy(false);
        }
    };

    const register = async () => {
        if (!groupName.trim()) {
            toast({ title: 'Enter a business/chain name', status: 'warning', duration: 3000 });
            return;
        }
        await call(() => axios.post(`${API_URL}/api/customer/commercial-group/register`, {
            laundryId, actingCustomerId: customerId,
            groupName: groupName.trim(), billingEmail: billingEmail.trim() || null,
            contactPhone: contactPhone.trim() || null,
            address: hqAddress.trim() || null,
            businessInfo: {
                contactName: contactName.trim() || undefined,
                businessType: businessType || undefined,
                estimatedLocations: estLocations ? Number(estLocations) : undefined,
                notes: notes.trim() || undefined,
                hqAddress: hqAddress.trim() || undefined,
                contactPhone: contactPhone.trim() || undefined,
            },
        }, authHeaders()), 'Commercial account created', 'Could not create account');
    };

    const addChild = async () => {
        if (!childLabel.trim()) {
            toast({ title: 'Enter a location name', status: 'warning', duration: 3000 });
            return;
        }
        if (!childAddress.trim()) {
            toast({ title: 'Enter the location delivery address', status: 'warning', duration: 3000 });
            return;
        }
        const ok = await call(() => axios.post(`${API_URL}/api/customer/commercial-group/child`, {
            laundryId, actingCustomerId: customerId, groupId: group.groupId,
            locationLabel: childLabel.trim(),
            contactName: childLabel.trim(),
            email: childEmail.trim() || null,
            phone: childPhone.trim() || null,
            address: childAddress.trim(),
        }, authHeaders()), 'Location added', 'Could not add location');
        if (ok) { setChildLabel(''); setChildEmail(''); setChildPhone(''); setChildAddress(''); }
    };

    const openEdit = (c) => {
        setEditing(c);
        setEditLabel(c.locationLabel || '');
        setEditEmail(c.email || '');
        setEditPhone(c.phone || '');
        setEditAddress(c.address || '');
        editModal.onOpen();
    };

    const saveEdit = async () => {
        if (!editing) return;
        const payload = {
            laundryId, actingCustomerId: customerId, groupId: group.groupId,
            childCustomerId: editing.customerId,
            locationLabel: editLabel.trim() || null,
            email: editEmail.trim() || null,
            phone: editPhone.trim() || null,
        };
        if (editAddress.trim() && editAddress.trim() !== (editing.address || '')) {
            payload.address = editAddress.trim();
        }
        const ok = await call(() => axios.patch(`${API_URL}/api/customer/commercial-group/child`, payload, authHeaders()),
            'Location updated', 'Could not update location');
        if (ok) { editModal.onClose(); setEditing(null); }
    };

    const removeChild = async (c) => {
        if (!window.confirm(`Remove ${c.locationLabel || 'this location'}? Its past orders and invoices are kept.`)) return;
        await call(() => axios.delete(`${API_URL}/api/customer/commercial-group/child`, {
            params: { laundryId, actingCustomerId: customerId, groupId: group.groupId, childCustomerId: c.customerId },
            ...authHeaders(),
        }), 'Location removed', 'Could not remove location');
    };

    const submitForApproval = async () => {
        await call(() => axios.post(`${API_URL}/api/customer/commercial-group/submit`, {
            groupId: group.groupId, laundryId, actingCustomerId: customerId,
        }, authHeaders()), 'Submitted for approval', 'Could not submit');
    };

    if (loading) {
        return <Container maxW="container.md" py={10}><HStack><Spinner /><Text>Loading…</Text></HStack></Container>;
    }

    // Not in a group → offer to register
    if (!inGroup) {
        return (
            <Container maxW="container.md" py={8}>
                <Heading size="lg" mb={2}>Commercial Account</Heading>
                <Text color="gray.600" mb={6}>
                    Manage laundry for multiple locations under one account. Create your account,
                    add your locations, and submit for the laundry to approve. You can place orders
                    for any location once approved, and get one invoice per pickup day covering all of them.
                </Text>
                <Card><CardBody>
                    <VStack spacing={4} align="stretch">
                        <FormControl isRequired>
                            <FormLabel>Business / chain name</FormLabel>
                            <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="e.g. Bright Salons" />
                        </FormControl>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl>
                                <FormLabel>Contact name</FormLabel>
                                <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Jane Smith" />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Type of business</FormLabel>
                                <Select placeholder="Select…" value={businessType} onChange={(e) => setBusinessType(e.target.value)}>
                                    {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Billing email (where invoices go)</FormLabel>
                                <Input type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} placeholder="billing@brightsalons.com" />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Contact phone</FormLabel>
                                <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="(555) 123-4567" />
                            </FormControl>
                            <FormControl>
                                <FormLabel>How many locations?</FormLabel>
                                <Input type="number" min={1} value={estLocations} onChange={(e) => setEstLocations(e.target.value)} placeholder="5" />
                            </FormControl>
                        </SimpleGrid>
                        <FormControl>
                            <FormLabel>Headquarters address (optional)</FormLabel>
                            <AddressAutocompleteInput size="md" value={hqAddress} onChange={setHqAddress} placeholder="123 Main St, City, ST 78664" />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Anything the laundry should know? (optional)</FormLabel>
                            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Weekly towel pickups, ~40 lbs per location" />
                        </FormControl>
                        <Button colorScheme="blue" onClick={register} isLoading={busy}>Create commercial account</Button>
                    </VStack>
                </CardBody></Card>
            </Container>
        );
    }

    // In a group but not the parent (a child landed here) → redirect to their orders
    if (role !== 'parent_admin') {
        return (
            <Container maxW="container.md" py={10}>
                <Alert status="info" borderRadius="md"><AlertIcon />
                    This location is managed by {group?.groupName || 'your commercial account'}. Use your Orders page to view and manage your orders.
                </Alert>
                <Button mt={4} onClick={() => navigate(`/${laundryId}/user/my-orders`)}>Go to my orders</Button>
            </Container>
        );
    }

    const status = group?.status || 'draft';
    const statusMeta = STATUS_LABEL[status] || STATUS_LABEL.draft;
    const children = (group?.members || []).filter((m) => m.role === 'child_manager');
    const canEditChildren = ['draft', 'pending_approval', 'active', 'rejected'].includes(status);
    const canSubmit = status === 'draft' || status === 'rejected';
    const canOrder = status === 'active';

    return (
        <Container maxW="container.lg" py={8}>
            <HStack justify="space-between" mb={2} flexWrap="wrap">
                <Heading size="lg">{group.groupName}</Heading>
                <Badge colorScheme={statusMeta.scheme} fontSize="0.9em" px={3} py={1} borderRadius="full">
                    {statusMeta.text}
                </Badge>
            </HStack>
            <Text color="gray.600" mb={4}>Billing email: {group.billingEmail || '—'}</Text>

            {status === 'draft' && (
                <Alert status="info" borderRadius="md" mb={4}><AlertIcon />
                    Add your locations below, then submit your account for the laundry to approve.
                </Alert>
            )}
            {status === 'pending_approval' && (
                <Alert status="warning" borderRadius="md" mb={4}><AlertIcon />
                    Your account is awaiting the laundry's approval. You can keep editing locations; ordering unlocks once approved.
                </Alert>
            )}
            {status === 'rejected' && (
                <Alert status="error" borderRadius="md" mb={4} alignItems="flex-start"><AlertIcon />
                    <Box>
                        <Text>Your account wasn't approved. Update your locations and re-submit, or contact the laundry.</Text>
                        {group.rejectedReason && <Text mt={1} fontWeight="semibold">Reason: {group.rejectedReason}</Text>}
                    </Box>
                </Alert>
            )}
            {status === 'suspended' && (
                <Alert status="warning" borderRadius="md" mb={4}><AlertIcon />
                    The laundry has paused this account, so new orders are on hold. Existing orders are not affected. Please contact the laundry.
                </Alert>
            )}
            {status === 'active' && (
                <Alert status="success" borderRadius="md" mb={4}><AlertIcon />
                    Approved — place and manage orders for each location below. All locations picked up on the same day are billed on one invoice, sent to your billing email.
                </Alert>
            )}

            <Divider my={4} />

            <HStack justify="space-between" mb={3} flexWrap="wrap">
                <Heading size="md">Locations</Heading>
                {canOrder && (
                    <Button size="sm" variant="outline" onClick={() => navigate(`/${laundryId}/user/my-orders`)}>
                        View all location orders
                    </Button>
                )}
            </HStack>
            <Box overflowX="auto" mb={4}>
                <Table size="sm" variant="simple">
                    <Thead><Tr><Th>Location</Th><Th>Address</Th><Th>Contact</Th><Th textAlign="right">Actions</Th></Tr></Thead>
                    <Tbody>
                        {children.length === 0 && (
                            <Tr><Td colSpan={4}><Text color="gray.500">No locations yet. Add your first location below.</Text></Td></Tr>
                        )}
                        {children.map((c) => (
                            <React.Fragment key={c.customerId}>
                                <Tr>
                                    <Td>
                                        <Text>{c.locationLabel || c.name || '—'}</Text>
                                        {c.isSuspended && <Badge colorScheme="orange" mt={1}>Paused by laundry</Badge>}
                                    </Td>
                                    <Td><Text fontSize="xs" color="gray.600">{c.address || '—'}</Text></Td>
                                    <Td><Text fontSize="xs" color="gray.600">{c.email || c.phone || '—'}</Text></Td>
                                    <Td textAlign="right">
                                        <HStack justify="flex-end" spacing={1}>
                                            {canOrder && !c.isSuspended && (
                                                <Button size="xs" colorScheme="blue" variant="outline"
                                                    onClick={() => navigate(`/${laundryId}/user?forCustomerId=${encodeURIComponent(c.customerId)}`)}>
                                                    Place order
                                                </Button>
                                            )}
                                            {canOrder && (
                                                <Button size="xs" variant="ghost"
                                                    onClick={() => setScheduleFor(scheduleFor === c.customerId ? null : c.customerId)}>
                                                    Schedule
                                                </Button>
                                            )}
                                            {canEditChildren && (
                                                <IconButton aria-label="Edit location" icon={<EditIcon />} size="xs"
                                                    variant="ghost" isDisabled={busy} onClick={() => openEdit(c)} />
                                            )}
                                            {canEditChildren && (
                                                <IconButton aria-label="Remove location" icon={<DeleteIcon />} size="xs"
                                                    colorScheme="red" variant="ghost" isDisabled={busy}
                                                    onClick={() => removeChild(c)} />
                                            )}
                                        </HStack>
                                    </Td>
                                </Tr>
                                {canOrder && scheduleFor === c.customerId && (
                                    <Tr><Td colSpan={4} bg="gray.50">
                                        <Collapse in animateOpacity>
                                            <SubscriptionCard
                                                customerId={c.customerId}
                                                laundryId={laundryId}
                                                actingCustomerId={customerId}
                                                title={`${c.locationLabel || 'Location'} — recurring pickup`}
                                                emptyText="No recurring pickup for this location. Choose a frequency when you place an order to set one up."
                                            />
                                        </Collapse>
                                    </Td></Tr>
                                )}
                            </React.Fragment>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            {canEditChildren && (
                <Card mb={4}><CardBody>
                    <Heading size="sm" mb={3}>Add a location</Heading>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                        Enter the location's name, contact, and delivery address. We'll check the
                        ZIP is in the delivery area — if it isn't, we'll add it to your ZIP demand list.
                    </Text>
                    <VStack spacing={3} align="stretch">
                        <HStack spacing={3} align="flex-end" flexWrap="wrap">
                            <FormControl maxW="240px" isRequired>
                                <FormLabel fontSize="sm">Location name</FormLabel>
                                <Input size="sm" value={childLabel} onChange={(e) => setChildLabel(e.target.value)} placeholder="Downtown Salon" />
                            </FormControl>
                            <FormControl maxW="240px">
                                <FormLabel fontSize="sm">Email (optional)</FormLabel>
                                <Input size="sm" value={childEmail} onChange={(e) => setChildEmail(e.target.value)} placeholder="downtown@brightsalons.com" />
                            </FormControl>
                            <FormControl maxW="200px">
                                <FormLabel fontSize="sm">Phone (optional)</FormLabel>
                                <Input size="sm" value={childPhone} onChange={(e) => setChildPhone(e.target.value)} placeholder="(555) 123-4567" />
                            </FormControl>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                            Leave email and phone blank to get this location's updates yourself. Add them only if someone
                            at the location should sign in and get updates.
                        </Text>
                        <HStack spacing={3} align="flex-end" flexWrap="wrap">
                            <FormControl flex="1" minW="280px" isRequired>
                                <FormLabel fontSize="sm">Delivery address (include ZIP)</FormLabel>
                                <AddressAutocompleteInput value={childAddress} onChange={setChildAddress} placeholder="456 Oak Ave, City, ST 78664" />
                            </FormControl>
                            <Button size="sm" leftIcon={<AddIcon />} colorScheme="blue" onClick={addChild} isLoading={busy}>Add location</Button>
                        </HStack>
                    </VStack>
                </CardBody></Card>
            )}

            {canSubmit && (
                <Button colorScheme="green" onClick={submitForApproval} isLoading={busy} isDisabled={children.length === 0}>
                    {status === 'rejected' ? 'Re-submit for approval' : 'Submit for approval'}
                </Button>
            )}

            {isActiveParent && (
                <>
                    <Divider my={6} />
                    <HStack justify="space-between" mb={3} flexWrap="wrap" spacing={3}>
                        <Heading size="md">Spend by location</Heading>
                        <HStack spacing={2} flexWrap="wrap">
                            <Button size="xs" variant="outline" onClick={() => setRange(monthRange(0))}>This month</Button>
                            <Button size="xs" variant="outline" onClick={() => setRange(monthRange(-1))}>Last month</Button>
                            <Input size="xs" type="date" maxW="150px" value={range.start}
                                onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} />
                            <Text fontSize="xs">to</Text>
                            <Input size="xs" type="date" maxW="150px" value={range.end}
                                onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} />
                        </HStack>
                    </HStack>
                    {breakdownLoading ? <HStack><Spinner size="sm" /><Text>Loading…</Text></HStack> : (
                        <>
                            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mb={3}>
                                <Stat><StatLabel>Locations</StatLabel><StatNumber>{(breakdown?.children || []).length}</StatNumber></Stat>
                                <Stat><StatLabel>Orders</StatLabel><StatNumber>{(breakdown?.children || []).reduce((n, c) => n + (c.orderCount || 0), 0)}</StatNumber></Stat>
                                <Stat><StatLabel>Total</StatLabel><StatNumber>${Number(breakdown?.groupTotal || 0).toFixed(2)}</StatNumber></Stat>
                            </SimpleGrid>
                            <Table size="sm" variant="simple">
                                <Thead><Tr><Th>Location</Th><Th isNumeric>Orders</Th><Th isNumeric>Amount</Th></Tr></Thead>
                                <Tbody>
                                    {(breakdown?.children || []).length === 0 && (
                                        <Tr><Td colSpan={3}><Text color="gray.500">No orders in this period.</Text></Td></Tr>
                                    )}
                                    {(breakdown?.children || []).map((c) => (
                                        <Tr key={c.customerId}>
                                            <Td>{c.locationLabel}</Td>
                                            <Td isNumeric>{c.orderCount}</Td>
                                            <Td isNumeric>${Number(c.revenue || 0).toFixed(2)}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </>
                    )}
                </>
            )}

            <Modal isOpen={editModal.isOpen} onClose={editModal.onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit location</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={3} align="stretch">
                            <FormControl>
                                <FormLabel fontSize="sm">Location name</FormLabel>
                                <Input size="sm" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
                            </FormControl>
                            <HStack spacing={3}>
                                <FormControl>
                                    <FormLabel fontSize="sm">Email</FormLabel>
                                    <Input size="sm" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Phone</FormLabel>
                                    <Input size="sm" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
                                </FormControl>
                            </HStack>
                            <FormControl>
                                <FormLabel fontSize="sm">Delivery address</FormLabel>
                                <AddressAutocompleteInput value={editAddress} onChange={setEditAddress} placeholder="456 Oak Ave, City, ST 78664" />
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    A new address applies to future orders. Orders already placed keep their address.
                                </Text>
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={editModal.onClose}>Cancel</Button>
                        <Button colorScheme="blue" onClick={saveEdit} isLoading={busy}>Save</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Container>
    );
}
